import { useState, useEffect, useMemo } from 'react'
import { useUserSession } from 'features/session/context'
import { dateTimeStampFormat } from 'shared/util'

import {
  Box, BoxProps, Stack, Typography, Button
} from '@mui/material'

import { EditIcon, TrushIcon } from 'shared/icons'

import { ConfirmDialog } from 'components/dialog/confirm'
import { Loading } from 'components/controls/loading'

import { SearchResult, useDeleteSearch } from 'interface/search'
import { useGetManufacturerLogo } from 'interface/manufacturer'

import { EditSearchDialog } from 'features/search/dialog/editSearchDialog'
import { UserAvatar } from 'features/user/components/avatar'

type SearchHeaderProps = {
  search: SearchResult
	onChangeSearch: (result: SearchResult) => void
  onDeleteSearch: () => void
  isSmallMedia: boolean
} & BoxProps
export const SearchHeader = ({
  search,
  onChangeSearch,
  onDeleteSearch,
  isSmallMedia,
  ...others
}: SearchHeaderProps) => {
  const { isAdmin, isGeneral } = useUserSession()
  const [manufacturerLogoImage, setManufacturerLogoImage] = useState<string | undefined>(undefined)
  const [modal, setModal] = useState<'editSearch' | 'deleteSearchConfirm' | null>(null)

  const manufacturer = useMemo(
    () => search?.manufacturer ? {
      manufacturerId: search.manufacturer.manufacturerId,
      updateTime: search.manufacturer.updateTime
    } : undefined,
    [search]
  )
  
  const manufacturerLogoState = useGetManufacturerLogo(manufacturer?.manufacturerId, manufacturer?.updateTime)
  useEffect(
    () => {
      setManufacturerLogoImage(manufacturerLogoState.data ? URL.createObjectURL(manufacturerLogoState.data.blob) : undefined)
    },
    [manufacturerLogoState.data]
  )

  const [deleteSearchState, deleteSearch] = useDeleteSearch({ onDeleted: onDeleteSearch })

  if (deleteSearchState.posting) return <Loading/>
  return (
    <Box {...others} position="relative">
      <Stack direction="row" padding={1} spacing={1} alignItems="center" justifyContent="center" flexWrap="wrap" useFlexGap>
        <Stack flexGrow={1} direction="row" spacing={2} justifyContent="center" alignItems="center">
          {manufacturerLogoImage && <img alt={search.title} src={manufacturerLogoImage} style={{ borderRadius: 1, width: '100px' }}/> }
          <Typography component="div" variant={isSmallMedia ? "h5" : "h4"} color="text.primary" fontWeight="bold" paddingY={1}>
            {search.title}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
          {search.updateUserId && (
            <UserAvatar
              userId={search.updateUserId}
              userName={search.updateUserName ?? undefined}
              toolTip={(
                <div>
                  {search.updateUserName}<br/>
                  {dateTimeStampFormat(search.updateTime)} 更新
                </div>
              )}
              sx={{ cursor: "help", height: '1.5rem', width: '1.5rem' }}
            />
          )}
          <Button
            disabled={!isGeneral}
            variant="outlined"
            size="medium"
            startIcon={<EditIcon/>}
            onClick={() => setModal('editSearch')}
          >
            編集
          </Button>
          <Button
            disabled={!isAdmin}
            variant="text"
            color="error"
            size="medium"
            startIcon={<TrushIcon/>}
            onClick={() => setModal('deleteSearchConfirm')}
          >
            削除
          </Button>
        </Stack>
      </Stack>

      {modal === 'editSearch' ? (
				<EditSearchDialog
					data={search}
					onSubmit={(result) => {
						setModal(null)
						onChangeSearch(result)
					}}
					onClose={() => setModal(null)}
				/>
			) : modal === 'deleteSearchConfirm' ? (
				<ConfirmDialog
					message={`${search.title}を削除します。よろしいですか？`}
          ok={{
            label: 'はい',
            onClick: () => {
              deleteSearch(search.searchId)
            }
          }}
          cancel={{
            label: 'いいえ',
            onClick: () => {
              setModal(null)
            }
          }}
				/>
			) : (
        <></>
      )}
    </Box>
  )
}